import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform, type AppStateStatus } from 'react-native';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { canRunOperationalBackgroundWork } from '../session/accountSetupService';
import { useSessionStore } from '../session/store/useSessionStore';
import { getHomeSnapshot } from '../home/homeSnapshotRepo';
import { countUnreadSupportReplies } from '../support/services/supportTicketsRepo';

type RefreshOpts = { force?: boolean };

type SupportUnreadContextValue = {
  count: number | null;
  refresh: (opts?: RefreshOpts) => Promise<void>;
};

const SupportUnreadContext = createContext<SupportUnreadContextValue | null>(null);

const POLL_MS = 60_000;

export function SupportUnreadProvider({ children }: { children: React.ReactNode }) {
  const userId = useAuthStore((s) => s.session?.user?.id);
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [count, setCount] = useState<number | null>(null);

  const refresh = useCallback(async (opts?: RefreshOpts) => {
    if (!userId || !canRunOperationalBackgroundWork()) {
      setCount(null);
      return;
    }
    try {
      // Con sucursal: el badge viene de la sección badges del home_snapshot
      // compartido (dedupe con Home y PendingOrdersCountProvider).
      // Sin sucursal: el count es user-scoped — path directo con su TTL propio.
      if (branchId) {
        const snap = await getHomeSnapshot(branchId, { force: opts?.force });
        setCount(snap?.badges?.supportUnread ?? null);
      } else {
        setCount(await countUnreadSupportReplies({ force: opts?.force !== false }));
      }
    } catch {
      setCount(null);
    }
  }, [branchId, userId]);

  useEffect(() => {
    if (userId && canRunOperationalBackgroundWork()) {
      void refresh({ force: true });
    } else {
      setCount(null);
    }
  }, [refresh, userId]);

  const visibleRef = useRef(true);
  useEffect(() => {
    const onActive = () => {
      // Sin force: con sucursal respeta el TTL de home_snapshot (15s); el poll a
      // 60s sigue refetcheando porque el TTL ya expiró en cada tick. Los refresh
      // explícitos (pantalla Mis reportes) siguen usando force.
      if (canRunOperationalBackgroundWork()) void refresh();
    };
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      visibleRef.current = next === 'active';
      if (next === 'active') onActive();
    });
    let removeVis: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVis = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) onActive();
      };
      document.addEventListener('visibilitychange', onVis);
      visibleRef.current = document.visibilityState === 'visible';
      removeVis = () => document.removeEventListener('visibilitychange', onVis);
    }
    const interval = setInterval(() => {
      if (visibleRef.current) onActive();
    }, POLL_MS);
    return () => {
      sub.remove();
      removeVis?.();
      clearInterval(interval);
    };
  }, [refresh]);

  const value = useMemo(() => ({ count, refresh }), [count, refresh]);

  return <SupportUnreadContext.Provider value={value}>{children}</SupportUnreadContext.Provider>;
}

export function useSupportUnreadCount(): SupportUnreadContextValue {
  const ctx = useContext(SupportUnreadContext);
  if (!ctx) {
    throw new Error('useSupportUnreadCount debe usarse dentro de SupportUnreadProvider');
  }
  return ctx;
}
